import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/utils/cn';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  invalid?: boolean;
  /** Rendered inside the field, before the text — a search icon, a currency code. */
  prefix?: ReactNode;
  suffix?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}

const SIZES = {
  sm: 'h-8 text-body-sm',
  md: 'h-10 text-body',
  lg: 'h-12 text-card-title',
} as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, prefix, suffix, size = 'md', disabled, ...props }, ref) => (
    <div
      className={cn(
        'flex w-full items-center gap-2 rounded-md border bg-surface px-3 transition-colors',
        'focus-within:border-brand',
        invalid ? 'border-danger' : 'border-border hover:border-border-strong',
        disabled && 'cursor-not-allowed bg-surface-sunken',
        SIZES[size],
        className,
      )}
    >
      {prefix ? <span className="shrink-0 text-fg-tertiary">{prefix}</span> : null}
      <input
        ref={ref}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        className={cn(
          'min-w-0 flex-1 bg-transparent text-fg outline-none',
          'placeholder:text-fg-tertiary disabled:cursor-not-allowed disabled:text-fg-disabled',
        )}
        {...props}
      />
      {suffix ? <span className="shrink-0 text-fg-tertiary">{suffix}</span> : null}
    </div>
  ),
);

Input.displayName = 'Input';
